import { Requirement } from "types/requirement";
import { Tag, TAG_TYPE_ORG_EXCLUSIVE, getOrgExclusiveTagName } from "types/tag";

export function isOrgExclusiveTag(tag: Tag): boolean {
  return tag.type === TAG_TYPE_ORG_EXCLUSIVE;
}

type GetOrCreateOrgExclusiveTagDeps = {
  organizationName: string;
  tagIdByName: Record<string, string>;
  addTag: (options: {
    variables: { name: string; type: string };
  }) => Promise<unknown>;
  refreshTags: () => Promise<{
    data?: {
      tag?: Array<{ name?: string | null; tag_id?: string | null }> | null;
    } | null;
  }>;
};

export async function getOrCreateOrgExclusiveTagId(
  deps: GetOrCreateOrgExclusiveTagDeps,
): Promise<string | null> {
  const { organizationName, tagIdByName, addTag, refreshTags } = deps;
  if (!organizationName) return null;

  const exclusiveTagName = getOrgExclusiveTagName(organizationName);
  const existingId = tagIdByName[exclusiveTagName.toLowerCase()];
  if (existingId) return existingId;

  await addTag({
    variables: { name: exclusiveTagName, type: TAG_TYPE_ORG_EXCLUSIVE },
  });
  const refreshed = await refreshTags();
  return (
    refreshed.data?.tag?.find((tag) => tag.name === exclusiveTagName)?.tag_id ||
    null
  );
}

export function buildTagIdByName(
  tags: Array<{ name?: string | null; tag_id?: string | null }>,
): Record<string, string> {
  const map: Record<string, string> = {};
  tags.forEach((tag) => {
    if (tag.name && tag.tag_id) {
      map[tag.name.trim().toLowerCase()] = tag.tag_id;
    }
  });
  return map;
}

export function getRequirementOrgExclusiveTagName(
  requirement: Requirement,
): string | null {
  const tags =
    requirement.tags?.map((entry) => entry.tag).filter(Boolean) || [];
  const exclusive = tags.find((tag) => tag && isOrgExclusiveTag(tag));
  return exclusive?.name ?? null;
}

export function isRequirementAvailableForOrg(
  requirement: Requirement,
  currentOrgName?: string,
): boolean {
  const exclusiveTagName = getRequirementOrgExclusiveTagName(requirement);
  if (!exclusiveTagName) {
    return true;
  }
  if (!currentOrgName) {
    return false;
  }
  return exclusiveTagName === getOrgExclusiveTagName(currentOrgName);
}

export function filterRequirementsForOrgCatalog(
  requirements: Requirement[],
  currentOrgName?: string,
): Requirement[] {
  return requirements.filter((requirement) =>
    isRequirementAvailableForOrg(requirement, currentOrgName),
  );
}
